/**
 * Shared pieces of the paid-ads door family — the broadcast door (Step 4/5)
 * and the campaign door both render these, so an agent who used one instantly
 * knows the other. Extracted once, mounted twice; never forked.
 *
 * Every visual here is --lc-* tokens only (no-raw-hex.test enforces it) and
 * every string arrives from the caller's own copy module (house pattern:
 * per-feature en/ar copy).
 */
import { Input } from '@/components/ui/input'

/** Access-denied state: the machine code + message + upsell/admin copy. */
export function DoorDeniedState({
  title,
  message,
  code,
  upsell,
  testId = 'door-denied',
}: {
  title: string
  message: string
  code?: string
  upsell: string
  testId?: string
}) {
  return (
    <div
      role="alert"
      className="space-y-2 rounded-[var(--lc-radius-md)] border border-[var(--lc-border)] bg-[var(--lc-surface-sunken)] p-4"
      data-testid={testId}
    >
      <p className="font-medium text-[var(--lc-text-heading)]">{title}</p>
      <p className="text-sm text-[var(--lc-text-muted)]">{message}</p>
      {code ? <code className="text-xs text-[var(--lc-text-muted)]">{code}</code> : null}
      <p className="text-sm text-[var(--lc-text-muted)]">{upsell}</p>
    </div>
  )
}

/** Inline field error (caps violations, split sums, objective required…). */
export function DoorFieldError({ error, testId }: { error: string; testId: string }) {
  return (
    <p role="alert" className="text-sm text-[var(--lc-status-underOffer-fg)]" data-testid={testId}>
      {error}
    </p>
  )
}

export type DoorTargetingLabels = {
  country: string
  countryHelper?: string
  areas: string
  areasPlaceholder: string
  radius: string
}

/**
 * Territory-only targeting (Step-2 lockdown): country (locked to the tenant
 * market when one is resolved), optional area ids, optional radius. No other
 * dimension exists in any door UI.
 */
export function DoorTargetingFields({
  country,
  onCountryChange,
  areas,
  onAreasChange,
  radius,
  onRadiusChange,
  labels,
}: {
  country: string
  onCountryChange: (value: string) => void
  areas: string
  onAreasChange: (value: string) => void
  radius: string
  onRadiusChange: (value: string) => void
  labels: DoorTargetingLabels
}) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium text-[var(--lc-text-primary)]">{labels.country}</legend>
      <div className="space-y-2">
        <Input
          aria-label={labels.country}
          value={country}
          onChange={(event) => onCountryChange(event.target.value)}
          disabled={Boolean(labels.countryHelper)}
          className="bg-[var(--lc-surface-sunken)]"
        />
        {labels.countryHelper ? (
          <p className="text-xs text-[var(--lc-text-muted)]">{labels.countryHelper}</p>
        ) : null}
        <Input
          aria-label={labels.areas}
          value={areas}
          onChange={(event) => onAreasChange(event.target.value)}
          placeholder={labels.areasPlaceholder}
          className="bg-[var(--lc-surface-sunken)]"
        />
        <Input
          aria-label={labels.radius}
          inputMode="numeric"
          value={radius}
          onChange={(event) => onRadiusChange(event.target.value)}
          className="bg-[var(--lc-surface-sunken)]"
        />
      </div>
    </fieldset>
  )
}
